import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ProjectStatusBadge } from '../ProjectStatusBadge'
import { TechnologyMigrationPath } from './TechnologyMigrationPath'
import { TeamMemberChip } from './TeamMemberChip'
import { formatDateTime } from '@/lib/timeFormatters'
import type { Project, ProjectTeamMember } from '@/types'
import { getIndustryLabel, getTypeLabel } from '@/types'

interface ProjectDetailsCardProps {
  project: Project
  teamMembers: ProjectTeamMember[]
  canEdit: boolean
  onEdit: () => void
}

interface FieldProps {
  label: string
  children: React.ReactNode
  testId?: string
}

function Field({ label, children, testId }: FieldProps) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="text-sm">{children}</div>
    </div>
  )
}

export function ProjectDetailsCard({ project, teamMembers, canEdit, onEdit }: ProjectDetailsCardProps) {
  const createdLabel = formatDateTime(project.creationTime ?? null)

  return (
    <section
      className="rounded-lg border bg-card p-6"
      data-testid="project-details-card"
    >
      <div className="flex items-start justify-between mb-6">
        <h2 className="text-xl font-semibold">Project Details</h2>
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onEdit}
            aria-label="Edit project"
            data-testid="edit-project-button"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-y-6 md:grid-cols-4 md:gap-x-6 mb-6">
        <Field label="Project Name" testId="field-project-name">
          {project.name}
        </Field>
        <Field label="Industry" testId="field-industry">
          {getIndustryLabel(project.industry)}
        </Field>
        <Field label="Type" testId="field-type">
          {getTypeLabel(project.type)}
        </Field>
        <Field label="Client" testId="field-client">
          {project.client}
        </Field>

        <Field label="Status" testId="field-status">
          <ProjectStatusBadge status={project.status} />
        </Field>
        <Field label="Technologies" testId="field-technologies">
          <TechnologyMigrationPath
            from={project.technologiesFrom}
            to={project.technologiesTo}
          />
        </Field>
        <div className="md:col-span-1" />
        <Field label="Created" testId="field-created">
          {createdLabel}
        </Field>
      </div>

      <div className="mb-6">
        <Field label="Description" testId="field-description">
          <p className="leading-relaxed">{project.description ?? '—'}</p>
        </Field>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Team Members
        </p>
        {teamMembers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No team members assigned.</p>
        ) : (
          <div className="flex flex-wrap gap-4" data-testid="team-members-list">
            {teamMembers.map((member) => (
              <TeamMemberChip
                key={member.userId}
                name={member.displayName || member.email}
                roles={member.roles.map((r) => r.name)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
